import { createSelector } from '@reduxjs/toolkit';
import {
  AnalysisCalendar,
  CashFlow,
  CofidisDebtCollectionSynthesis,
  CofidisDebtConsolidationLoanAnalysis,
  CreditInsights,
  DebtCollectionSynthesis,
  DebtConsolidationLoanAnalysis,
  Transaction,
} from '@algoan/dashboard/model';
import {
  formatCofidisDebtCollectionIndicatorValues,
  formatCofidisDebtConsolidationIndicatorValues,
  formatDebtCollectionIndicatorValues,
  formatDebtCollectionSynthesis,
  formatDebtConsolidationIndicatorValues,
  formatDebtConsolidationSynthesis,
} from '../formatter/synthesis-formatter';
import {
  minimumNumberOfTransactionsToDisplayFrequentTransactionsSelector,
  accountsSelector,
  latestAnalysisSelector,
  transactionsSelector,
} from '@algoan/dashboard/store/selectors';
import { RootState } from '@algoan/dashboard/store/store';

export const debtCollectionSynthesisSelector = createSelector(
  accountsSelector,
  (state: RootState) => latestAnalysisSelector(state)?.creditInsights,
  transactionsSelector,
  (state: RootState) => latestAnalysisSelector(state)?.createdAt,
  (
    accounts,
    creditInsights?: CreditInsights,
    transactions?: Transaction[],
    analysisDate?: string,
  ): DebtCollectionSynthesis | undefined => {
    if (accounts && accounts.length > 0 && creditInsights && transactions && transactions.length > 0 && analysisDate) {
      return {
        ...formatDebtCollectionSynthesis(accounts, creditInsights.cashFlows),
        cashFlowIndicatorValues: formatDebtCollectionIndicatorValues(creditInsights, transactions),
      };
    }
    return undefined;
  },
);

export const cofidisDebtCollectionSynthesisSelector = createSelector(
  accountsSelector,
  (state: RootState) => latestAnalysisSelector(state)?.creditInsights,
  transactionsSelector,
  (state: RootState) => latestAnalysisSelector(state)?.createdAt,
  (
    accounts,
    creditInsights?: CreditInsights,
    transactions?: Transaction[],
    analysisDate?: string,
  ): CofidisDebtCollectionSynthesis | undefined => {
    if (accounts && accounts.length > 0 && creditInsights && transactions && transactions.length > 0 && analysisDate) {
      return {
        ...formatDebtCollectionSynthesis(accounts, creditInsights.cashFlows),
        cashFlowIndicatorValues: formatCofidisDebtCollectionIndicatorValues(creditInsights, transactions),
      };
    }
    return undefined;
  },
);

export const debtConsolidationSynthesisSelector = createSelector(
  (state: RootState) => minimumNumberOfTransactionsToDisplayFrequentTransactionsSelector(state),
  accountsSelector,
  (state: RootState) => latestAnalysisSelector(state)?.creditInsights,
  transactionsSelector,
  (state: RootState) => latestAnalysisSelector(state)?.creditInsights?.cashFlows,
  (state: RootState) =>
    latestAnalysisSelector(state)?.metadata?.creditInsights?.calendar ??
    latestAnalysisSelector(state)?.metadata?.calendar,
  (
    minimumNumberOfTransactionsToDisplayFrequentTransactions: number,
    accounts,
    creditInsights?: CreditInsights,
    transactions?: Transaction[],
    cashFlows?: CashFlow[],
    calendar?: AnalysisCalendar[],
  ): DebtConsolidationLoanAnalysis | undefined => {
    if (
      accounts &&
      accounts.length > 0 &&
      creditInsights &&
      transactions &&
      transactions.length > 0 &&
      cashFlows &&
      calendar
    ) {
      return {
        ...formatDebtConsolidationSynthesis(
          accounts,
          cashFlows,
          creditInsights,
          transactions,
          minimumNumberOfTransactionsToDisplayFrequentTransactions,
          calendar,
        ),
        cashFlowIndicatorValues: formatDebtConsolidationIndicatorValues(creditInsights, transactions),
      };
    }
    return undefined;
  },
);

export const cofidisDebtConsolidationSynthesisSelector = createSelector(
  (state: RootState) => minimumNumberOfTransactionsToDisplayFrequentTransactionsSelector(state),
  accountsSelector,
  (state: RootState) => latestAnalysisSelector(state)?.creditInsights,
  (state: RootState) =>
    latestAnalysisSelector(state)?.metadata?.creditInsights?.historicalDepth !== undefined
      ? latestAnalysisSelector(state)?.metadata?.creditInsights?.historicalDepth
      : latestAnalysisSelector(state)?.metadata?.historicalDepth,
  transactionsSelector,
  (state: RootState) => latestAnalysisSelector(state)?.createdAt,
  (state: RootState) => latestAnalysisSelector(state)?.creditInsights?.cashFlows,
  (state: RootState) =>
    latestAnalysisSelector(state)?.metadata?.creditInsights?.calendar ??
    latestAnalysisSelector(state)?.metadata?.calendar,
  (
    minimumNumberOfTransactionsToDisplayFrequentTransactions: number,
    accounts,
    creditInsights?: CreditInsights,
    historicalDepth?: number,
    transactions?: Transaction[],
    analysisDate?: string,
    cashFlows?: CashFlow[],
    calendar?: AnalysisCalendar[],
  ): CofidisDebtConsolidationLoanAnalysis | undefined => {
    if (
      accounts &&
      accounts.length > 0 &&
      creditInsights &&
      transactions &&
      transactions.length > 0 &&
      analysisDate &&
      cashFlows &&
      calendar
    ) {
      return {
        ...formatDebtConsolidationSynthesis(
          accounts,
          cashFlows,
          creditInsights,
          transactions,
          minimumNumberOfTransactionsToDisplayFrequentTransactions,
          calendar,
        ),
        cashFlowIndicatorValues: formatCofidisDebtConsolidationIndicatorValues(
          creditInsights,
          historicalDepth ?? 1,
          transactions,
          accounts,
          analysisDate,
        ),
      };
    }
    return undefined;
  },
);
